import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardSidebar from '../components/DashboardSidebar';
import { Button } from '../components/ui/button';
import { 
  FileText, Clock, CheckCircle, AlertCircle, 
  ArrowRight, Search, Filter, ShieldX, Upload, Pencil, Trash2
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { applicationsApi } from '../lib/api';
import CountryFlag from '../components/CountryFlag';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const MyApplications = () => {
  const { user } = useAuth();
  const isB2B = user?.role === 'b2b_partner';
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchApplications = async () => {
    try {
      const response = await applicationsApi.getMyApplications();
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleDelete = async (e, applicationId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    try {
      await applicationsApi.delete(applicationId);
      toast.success('Application deleted');
      fetchApplications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'rejected': return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'submitted':
      case 'under_review':
      case 'processing':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      default: return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'submitted':
      case 'under_review':
      case 'processing':
        return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = 
      app.application_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.country?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statuses = ['all', 'draft', 'submitted', 'under_review', 'processing', 'approved', 'rejected'];

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <DashboardSidebar />
      
      <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8" data-testid="my-applications-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              My Applications
            </h1>
            <p className="text-[#4A5568] mt-1">Track and manage all your visa applications</p>
          </div>
          <Link to="/apply">
            <Button className="bg-[#FF5A36] hover:bg-[#E0492B]" data-testid="new-application-btn">
              + New Application
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF] pointer-events-none" />
            <Input
              type="text"
              placeholder="Search by application ID or country..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12"
              data-testid="search-applications-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {statuses.map((status) => (
              <Button
                key={status}
                variant={statusFilter === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(status)}
                className={`capitalize ${statusFilter === status ? 'bg-[#0A1E40] text-white' : 'border-[#D1CBBF] text-[#4A5568] hover:text-[#0A1E40]'}`}
                data-testid={`filter-${status}`}
              >
                {status === 'all' ? 'All' : status.replace('_', ' ')}
              </Button>
            ))}
          </div>
        </div>

        {/* Applications List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-[#D1CBBF] animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-[#D1CBBF]">
            <FileText className="w-16 h-16 text-[#D1CBBF] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#0A1E40] mb-2">No applications found</h3>
            <p className="text-[#4A5568] mb-6">
              {searchQuery || statusFilter !== 'all' 
                ? 'Try adjusting your filters'
                : 'Start your visa journey today!'
              }
            </p>
            <Link to="/apply">
              <Button className="bg-[#FF5A36] hover:bg-[#E0492B]">
                Create Your First Application
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredApplications.map((app) => {
              const rejectedDocs = (app.documents || []).filter(d => d.verification_notes && !d.is_verified);
              return (
              <Link 
                key={app.application_id} 
                to={`/applications/${app.application_id}`}
                className="block"
                data-testid={`application-card-${app.application_id}`}
              >
                <div className="bg-white rounded-xl border border-[#D1CBBF] overflow-hidden card-hover">
                  {/* Rejected Documents Banner */}
                  {rejectedDocs.length > 0 && (
                    <div className="flex items-center gap-3 px-6 py-3 bg-red-50 border-b border-red-200" data-testid={`rejected-banner-${app.application_id}`}>
                      <ShieldX className="w-5 h-5 text-red-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-red-700">
                          {rejectedDocs.length} document{rejectedDocs.length > 1 ? 's' : ''} rejected — Re-upload required
                        </p>
                        <p className="text-xs text-red-600 truncate">
                          {rejectedDocs.map(d => d.document_type?.replace('_', ' ')).join(', ')}
                        </p>
                      </div>
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold shrink-0">
                        <Upload className="w-3.5 h-3.5" /> Action Needed
                      </span>
                    </div>
                  )}
                  
                  <div className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      {getStatusIcon(app.status)}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xl"><CountryFlag code={app.country?.code} size="md" /></span>
                          <h3 className="font-semibold text-[#0A1E40]">
                            {app.country?.name || 'Unknown Country'}
                          </h3>
                        </div>
                        <p className="text-sm text-[#4A5568]">
                          {app.visa_type?.name || 'Visa Application'} &bull; {app.application_id}
                        </p>
                        <p className="text-xs text-[#4A5568] mt-1">
                          {app.num_travelers} traveler{app.num_travelers > 1 ? 's' : ''} &bull; 
                          Travel date: {new Date(app.travel_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(app.status)}`}>
                          {app.status?.replace('_', ' ')}
                        </span>
                        <p className="text-xs text-[#4A5568] mt-1">
                          Created: {new Date(app.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      {app.payment_status !== 'completed' && (
                        <div className="flex items-center gap-1">
                          <Link
                            to={`${isB2B ? '/partner' : ''}/apply?edit=${app.application_id}`}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`edit-${app.application_id}`}
                          >
                            {app.status === 'draft' ? (
                              <Button size="sm" className="h-8 px-3 bg-[#FF5A36] hover:bg-[#E0492B] text-white text-xs font-semibold">
                                <Pencil className="w-3.5 h-3.5 mr-1" /> Resume
                              </Button>
                            ) : (
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#4A5568] hover:text-[#FF5A36]">
                                <Pencil className="w-4 h-4" />
                              </Button>
                            )}
                          </Link>
                          <Button
                            size="sm" variant="ghost"
                            className="h-8 w-8 p-0 text-[#4A5568] hover:text-red-500"
                            onClick={(e) => handleDelete(e, app.application_id)}
                            data-testid={`delete-${app.application_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      <ArrowRight className="w-5 h-5 text-[#D1CBBF]" />
                    </div>
                  </div>
                  </div>
                </div>
              </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default MyApplications;
